/**
 * Notification Service
 *
 * Handles all notification-related operations including fetching,
 * marking as read, and unread count tracking.
 *
 * @module services/notificationService
 */

import { supabase } from './supabase';

/**
 * Fetch notifications for the current user
 * @param {Object} options - Fetch options
 * @param {boolean} options.unreadOnly - Only fetch unread notifications
 * @param {number} options.limit - Max notifications to return
 * @param {number} options.offset - Pagination offset
 * @returns {Promise<{data, count, error}>}
 */
export const fetchNotifications = async ({ unreadOnly = false, limit = 20, offset = 0 } = {}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    let query = supabase
      .from('notifications')
      .select('*', { count: 'exact' })
      .eq('user_id', user.id);

    if (unreadOnly) {
      query = query.is('read_at', null);
    }

    query = query
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    const { data, count, error } = await query;

    if (error) throw error;

    return { data, count, error: null };
  } catch (error) {
    console.error('Fetch notifications error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get unread notification count for current user
 * @returns {Promise<{count, error}>}
 */
export const getUnreadCount = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .is('read_at', null);

    if (error) throw error;

    return { count, error: null };
  } catch (error) {
    console.error('Get unread count error:', error);
    return { count: 0, error };
  }
};

/**
 * Mark specific notifications as read
 * @param {string[]} notificationIds - Array of notification IDs to mark as read
 * @returns {Promise<{data, error}>}
 */
export const markAsRead = async (notificationIds) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .in('id', notificationIds)
      .eq('user_id', user.id)
      .select();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Mark as read error:', error);
    return { data: null, error };
  }
};

/**
 * Mark all notifications as read for current user
 * @returns {Promise<{count, error}>}
 */
export const markAllAsRead = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .is('read_at', null)
      .select('id');

    if (error) throw error;

    return { count: data?.length || 0, error: null };
  } catch (error) {
    console.error('Mark all as read error:', error);
    return { count: 0, error };
  }
};

/**
 * Create a notification for a specific user
 * DB column: notification_type (allowed: info, warning, error, success, task, approval)
 * DB column: action_url (for in-app navigation)
 * @param {Object} notification - Notification data
 * @param {string} notification.user_id - Target user ID
 * @param {string} notification.notification_type - DB notification_type (task, approval, info, etc.)
 * @param {string} notification.title - Notification title
 * @param {string} notification.message - Notification message
 * @param {string} [notification.action_url] - Optional in-app navigation URL
 * @param {string} [notification.related_type] - Optional related entity type (project, task, etc.)
 * @param {string} [notification.related_id] - Optional related entity ID
 * @returns {Promise<{data, error}>} `data` es la fila que se escribió, SIN el
 *   `id` que genera la base: leerlo de vuelta es justo lo que rompía (ver el
 *   comentario del insert).
 */
export const createNotification = async ({ user_id, notification_type, title, message, action_url, related_type, related_id }) => {
  try {
    const row = { user_id, notification_type, title, message };
    if (action_url) row.action_url = action_url;
    if (related_type) row.related_type = related_type;
    if (related_id) row.related_id = related_id;

    // SIN `.select()`. Esto decía `.insert([row]).select().single()` y por eso
    // esta función nunca pudo notificar a otra persona.
    //
    // La única policy de SELECT sobre `notifications` es
    // `user_own_notifications` (USING `user_id = auth.uid()`). Con RETURNING,
    // Postgres evalúa esa policy sobre la fila recién insertada; como acá
    // `user_id` es el del DESTINATARIO y no el del emisor, el statement ENTERO
    // aborta con 42501 y la fila no queda. El mensaje que vuelve —"new row
    // violates row-level security policy"— se lee como si hubiera rebotado el
    // WITH CHECK del INSERT, cuando el WITH CHECK sí había pasado. Ese es el
    // motivo por el que el bug sobrevivió: el error señala el lugar equivocado.
    //
    // Medido en prod el 2026-07-30 (service-role, Prefer:count=exact):
    //   notification_type IN ('task','approval','message','tool_lost_reported')
    //   -> 0 filas, contra 16.961 filas totales, todas escritas por emisores
    //   que saltean RLS (service-role y el trigger SECURITY DEFINER).
    //
    // Consecuencia deliberada: no se devuelve el `id` que genera la base,
    // porque leerlo es exactamente lo que rompe. Ningún llamador lo usaba.
    const { error } = await supabase
      .from('notifications')
      .insert([row]);

    if (error) throw error;

    return { data: row, error: null };
  } catch (error) {
    console.error('Create notification error:', error);
    return { data: null, error };
  }
};

const notificationService = {
  fetchNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  createNotification,
};

export default notificationService;
